import { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';

interface UniformCardProps {
  title: string;
  description: string;
  icon?: ReactNode;
  image?: string;
  imageAlt?: string;
  badges?: string[];
  badgeColors?: string[];
  ctaText: string;
  ctaLink?: string;
  ctaAction?: () => void;
  isExternal?: boolean;
  className?: string;
  variant?: 'default' | 'image-top' | 'icon-left';
}

const UniformCard = ({
  title,
  description,
  icon,
  image,
  imageAlt,
  badges = [],
  badgeColors = [],
  ctaText,
  ctaLink,
  ctaAction,
  isExternal = false,
  className = '',
  variant = 'default'
}: UniformCardProps) => {
  const handleClick = () => {
    if (ctaAction) {
      ctaAction();
    }
  };

  const renderCTA = () => {
    const buttonContent = (
      <>
        {ctaText}
        {isExternal ? (
          <ExternalLink className="ml-2 h-4 w-4" />
        ) : (
          <ArrowRight className="ml-2 h-4 w-4" />
        )}
      </>
    );

    if (ctaLink) {
      if (isExternal) {
        return (
          <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold flex items-center justify-center">
            <a href={ctaLink} target="_blank" rel="noopener noreferrer">
              {buttonContent}
            </a>
          </Button>
        );
      } else {
        return (
          <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold flex items-center justify-center">
            <Link to={ctaLink}>
              {buttonContent}
            </Link>
          </Button>
        );
      }
    }

    return (
      <Button 
        onClick={handleClick}
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold flex items-center justify-center"
      >
        {buttonContent}
      </Button>
    );
  };

  if (variant === 'image-top') {
    return (
      <Card className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group h-full ${className}`}>
        {image && (
          <div className="relative overflow-hidden rounded-t-lg">
            <img 
              src={image} 
              alt={imageAlt || title}
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        
        <CardContent className="p-6">
          <div className="flex flex-col h-full min-h-[200px]">
            {/* Header avec titre */}
            <div className="mb-3">
              <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                {title}
              </h3>
            </div>
            
            {/* Description avec flex-grow pour pousser le reste vers le bas */}
            <p className="text-slate-700 leading-relaxed mb-4 flex-grow text-sm line-clamp-3">
              {description}
            </p>
            
            {/* Badges - hauteur fixe pour alignement */}
            <div className="mb-6 min-h-[32px] flex items-start">
              <div className="flex flex-wrap gap-2">
                {badges.map((badge, index) => (
                  <span 
                    key={index}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      badgeColors[index] || 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
            
            {/* CTA toujours en bas */}
            <div className="mt-auto">
              {renderCTA()}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'icon-left') {
    return (
      <Card className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white h-full ${className}`}>
        <CardContent className="p-8">
          <div className="flex flex-col h-full min-h-[240px]">
            <div className="flex items-start mb-6">
              {icon && (
                <div className="p-3 rounded-xl mr-4 bg-primary/10 text-primary flex-shrink-0">
                  {icon}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-slate-900 line-clamp-2 flex-1">
                    {title}
                  </h3>
                  {badges.length > 0 && (
                    <span className={`ml-3 px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${
                      badgeColors[0] || 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {badges[0]}
                    </span>
                  )}
                </div>
                <p className="text-slate-600 leading-relaxed mb-6 flex-grow line-clamp-3">
                  {description}
                </p>
              </div>
            </div>
            <div className="mt-auto">
              {renderCTA()}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default variant
  return (
    <Card className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group h-full ${className}`}>
      <CardContent className="p-6 text-center flex flex-col h-full min-h-[320px]">
        {icon && (
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
            {icon}
          </div>
        )}
        
        <h3 className="text-lg font-bold text-slate-900 mb-3 line-clamp-2">
          {title}
        </h3>
        
        <p className="text-slate-600 mb-4 leading-relaxed flex-grow text-sm line-clamp-3">
          {description}
        </p>
        
        {badges.length > 0 && (
          <div className="mb-4 min-h-[24px] flex justify-center">
            <div className="flex flex-wrap gap-2 justify-center">
              {badges.map((badge, index) => (
                <span 
                  key={index}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    badgeColors[index] || 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-auto">
          {renderCTA()}
        </div>
      </CardContent>
    </Card>
  );
};

export default UniformCard;